import React, { useState, useEffect, useRef } from 'react';
import { FaHeadphonesSimple } from "react-icons/fa6";
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';

import '../../src/assets/css/style.css';
import CustomLine from './CustomLine';
import AppoinmentModal from './AppoinmnetModal';
import InquiryModal from './DashboardLayouts/InquiryModal';
import { getSliders } from '../api/api';

const HomeBanner = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [slideIn, setSlideIn] = useState(true);
    const [sliders, setSliders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isMounted, setIsMounted] = useState(false);
    const [isInquiryModalOpen, setIsInquiryModalOpen] = useState(false);

    const stickyButtonRef = useRef(null);
    const navigate = useNavigate();

    // Fetch sliders from API
    useEffect(() => {
        const fetchSliders = async () => {
            try {
                const response = await getSliders();
                if (response.status && response.data && response.data.length > 0) {
                    setSliders(response.data);
                }
            } catch (error) {
                console.error('Error fetching sliders:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchSliders();
    }, []);

    const handleScroll = () => {
        const stickyButton = stickyButtonRef.current;
        const scrollY = window.scrollY;

        if (stickyButton) {
            if (scrollY > 200) {
                stickyButton.style.bottom = '50%';
            }
        }
    };

    const handleContactClick = () => {
        navigate('/contact');
    };

    const openInquiryModal = () => {
        setIsInquiryModalOpen(true);
    };

    const closeInquiryModal = () => {
        setIsInquiryModalOpen(false);
    };

    useEffect(() => {
        setIsMounted(true);
        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const nextSlide = () => {
        if (sliders.length === 0) return;
        setSlideIn(false);
        setTimeout(() => {
            setCurrentIndex((prevIndex) => (prevIndex === sliders.length - 1 ? 0 : prevIndex + 1));
            setTimeout(() => {
                setSlideIn(true);
            }, 50);
        }, 300);
    };

    useEffect(() => {
        if (sliders.length === 0) return;
        const imageInterval = setInterval(nextSlide, 7000);
        return () => clearInterval(imageInterval);
    }, [sliders]);

    useEffect(() => {
        setSlideIn(false);
        const timer = setTimeout(() => {
            setSlideIn(true);
        }, 50);
        return () => clearTimeout(timer);
    }, [currentIndex]);

    const openModal = () => {
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    // Function to split title into parts for line breaks
    const formatTitle = (title) => {
        if (!title) return '';
        
        // For "Your Journey Begins with Understanding Who You Are"
        if (title.includes('Your Journey Begins with Understanding Who You Are')) {
            return (
                <>
                    Your Journey Begins<br /> with Understanding Who You Are
                </>
            );
        }
        
        // For "Empowering You to Unlock Your Genetic Potential"
        if (title.includes('Empowering You to Unlock Your Genetic Potential')) {
            return (
                <>
                    Empowering You to Unlock<br />Your Genetic Potential
                </>
            );
        }
        
        // For "A Personal Approach to Wellness"
        if (title.includes('A Personal Approach to Wellness')) {
            return title;
        }
        
        // For "Personalised Gene Based Counselling"
        if (title.includes('Personalised Gene Based Counselling')) {
            return (
                <>
                    Personalised Gene Based<br />Counselling
                </>
            );
        }
        
        // Default: split long titles
        if (title.length > 30) {
            const words = title.split(' ');
            const mid = Math.floor(words.length / 2);
            return (
                <>
                    {words.slice(0, mid).join(' ')}<br />{words.slice(mid).join(' ')}
                </>
            );
        }
        
        return title;
    };

    // Show nothing while loading or if no sliders
    if (loading || sliders.length === 0) {
        return null;
    }

    // Get current slider data
    const currentSlider = sliders[currentIndex];

    return (
        <>
            <section className="relative w-full main-padding-mobile pt-4 sm:pt-0 sm:px-0">
                <div className="relative w-full overflow-hidden">
                    <img
                        className="w-full h-[38vh] sm:h-auto object-cover"
                        src={currentSlider?.imageUrl || currentSlider?.image}
                        alt={currentSlider?.title || 'Slide'}
                    />
                    <div
                        className={`custom-padding xl-screen-padding absolute inset-0 flex flex-col justify-center text-left text-white buttons-container-space`}
                    >
                        {currentSlider && (
                            <>
                                {/* Only show "Welcome to Nutrillion" on first slide (index 0) */}
                                {currentIndex === 0 && (
                                    <p className={`custom-padding-bottom text-[#7C517E] text-md sm:text-md font-lato block sm:flex items-center ${slideIn ? 'slide-left' : 'slide-out'}`}>
                                        <CustomLine />
                                        <span className="ml-0 sm:ml-2">Welcome to Nutrillion</span>
                                    </p>
                                )}
                                
                                <h1 className={`custom-padding-bottom text-lg sm:text-xl custom-line-height md:text-xl lg:text-4xl font-semibold text-black leading-tight client-text ${slideIn ? 'slide-left' : 'slide-out'}`}>
                                    {formatTitle(currentSlider.title)}
                                </h1>
                                <hr className={`custom-padding-bottom border-t-2 border-[#6DBE45] w-3/5 sm:w-2/3 md:w-2/5 lg:w-1/3 xl:w-1/4 2xl:w-1/5 sm:block ${slideIn ? 'slide-left' : 'slide-out'}`} />
                                <p className={`custom-padding-bottom text-[#7C517E] text-md sm:text-md font-lato sm:block ${slideIn ? 'slide-left' : 'slide-out'}`}>
                                    {currentSlider.sub_title}
                                </p>
                            </>
                        )}
                    </div>

                    <div className="absolute buttons-container flex flex-row space-x-3">
                        <Link to="/service">
                            <button className="bg-gray-800 common-btn-all-second text-white font-lato text-[10px] sm:text-sm md:text-[12px] px-3 py-1 sm:px-6 sm:py-2 md:px-6 md:py-3 rounded-full w-[120px] sm:w-auto md:w-[150px] lg:w-[120px]">
                                Read More
                            </button>
                        </Link>

                        <button 
                            className="bg-[#8f762c] common-btn-all text-white font-lato text-[10px] sm:text-sm md:text-[12px] px-3 py-1 sm:px-6 sm:py-2 md:px-6 md:py-3 rounded-full w-[120px] sm:w-auto md:w-[150px] lg:w-[120px]"
                            onClick={handleContactClick}
                        >
                            Contact Us
                        </button>
                        <AppoinmentModal isOpen={isModalOpen} onClose={closeModal} />
                    </div>
                </div>
            </section>

            <div>
                <button
                    ref={stickyButtonRef}
                    type="button"
                    className="sticky-button bg-[#7c517e] text-white text-sm px-3 py-1 md:px-4 md:py-2 mt-2 md:mt-0 w-full cursor-pointer md:w-auto"
                    onClick={openModal}
                >
                    BOOK APPOINTMENT
                </button>
                <AppoinmentModal isOpen={isModalOpen} onClose={closeModal} />
            </div>

            <section className="main-padding-mobile 2xl:custom-padding-xlscreen custom-padding xl-screen-padding custom-padding-bottom md:px-10 grid grid-cols-1 md:grid-cols-12 gap-4 mx-auto z-10 relative mt-6 lg:mt-[-19vh]">
                <div className="md:col-span-4 text-white rounded-t-lg rounded-b-none relative overflow-hidden p-4 br-10">
                    <img
                        src={require('../../src/assets/images/image_yellow.png')}
                        alt="Call Background"
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="relative z-10 flex flex-col items-center justify-center h-full text-center">
                        <div className="flex items-center mt-0 justify-center">
                            <FaHeadphonesSimple className="text-3xl mr-2" />
                            <h3 className="text-lg font-roboto">Give Us A Call</h3>
                        </div>
                        <p className="custom-padding-bottom text-lg font-lato custom-">+91 955 743 9399</p>
                        <p className="custom-padding-bottom text-center font-lato">Take the first step towards a healthy life</p>
                        <button
                            onClick={openInquiryModal}
                            className="bg-[] px-4 py-2 font-semibold rounded-full border-1 text-white text-xs common-btn-all"
                            style={{ border: '1px solid #ffffff' }}
                            onMouseEnter={(e) => e.target.style.borderColor = '#7c517e'}
                            onMouseLeave={(e) => e.target.style.borderColor = '#ffffff'}
                        >
                            ENQUIRE TODAY
                        </button>
                    </div>
                </div>

                <div className="md:col-span-8 pl-15 bg-white rounded-lg shadow-lg flex flex-col md:flex-row relative p-4 sm:p-0 md:p-0 lg:p-0 xl:p-0 2xl:p-0">
                    <div className="flex-1 pt-80 pb-80">
                        <h3
                            className={`custom-padding-bottom text-h2 font-semibold text-gray-800 client-text ${isMounted ? 'typewriter-effect' : ''}`}
                        >
                            <div>
                                <p>
                                    Meet our <span className="text-purple-500 tablet-break">Expert Clinical Dietitians</span>
                                </p>
                            </div>
                        </h3>
                        <p className="custom-padding-bottom font-barlow">
                            <span className="text-[#555555] text-p">Dedicated to counselling you toward </span><br />
                            <span className="text-[#8c678e] text-p">healthier, more balanced lifestyle</span>
                        </p>
                        <button
                            onClick={openInquiryModal}
                            className="text-white font-semibold text-sm px-4 py-2 rounded common-btn-all mb-4 sm:mb-0"
                        >
                            Get in Touch
                        </button>
                    </div>
                    <div className="flex-1 md:mt-0 md:ml-0 relative mt-4 md:mt-0">
                        <div className="relative">
                            <img
                                src={require('../../src/assets/images/expert.webp')}
                                alt="Appointment"
                                className="h-72 w-full object-cover home-img home-img-border"
                            />
                        </div>
                    </div>
                </div>
            </section>

            <InquiryModal
                isOpen={isInquiryModalOpen}
                onClose={closeInquiryModal}
            />
        </>
    );
};

export default HomeBanner;